import { Component, ViewChild, effect, inject } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { PaymentJustificationService } from './payment-justification.service';
import { PaymentJustificationRepository } from '@/store/payment-justification-state/payment-justification-state.repository';
import { getPaymentJustificationDataTableStructure } from './utils/payment-justification-data-table-object';
import { getPaymentJustificationDetailDrawerObject } from './utils/payment-justification-detail-drawer-object';
import { getApproveConfirmDialog, getRejectConfirmDialog } from './utils/payment-justification-dialog-objects';
import { SocieteDacListItem } from '@/types';

@Component({
  selector: 'sm-payment-justification',
  standalone: true,
  imports: [PageHeaderComponent, DataTableBuilderComponent, DrawerComponent, DialogComponent, TranslateModule],
  templateUrl: './payment-justification.component.html',
  styleUrl: './payment-justification.component.scss',
})
export class PaymentJustificationComponent {
  translate = inject(TranslateService);
  messageService = inject(MessageService);
  service = inject(PaymentJustificationService);
  store = inject(PaymentJustificationRepository);

  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  @ViewChild('dialog') dialogComponent!: DialogComponent;

  detailDrawer?: DrawerObject;
  dialogObject?: DialogObject;

  breadcrumb = [
    { label: 'settings.paymentJustification.breadcrumb.settings', routerLink: ['/settings'] },
    { label: 'settings.paymentJustification.breadcrumb.paymentJustification', routerLink: ['/settings', 'payment-justification'] },
  ];

  // Data Table
  data: SocieteDacListItem[] = [];
  totalRecords = 0;
  loading = true;
  serverQuery = createServerQuery({});

  dataTableObject = getPaymentJustificationDataTableStructure({
    store: this.store,
    serverQuery: this.serverQuery,
    enableServerActions: true,
    openDrawerArgs: row => this.openDetailDrawer(row),
    rowActions: {
      inspectAction: row => this.openDetailDrawer(row),
    },
  });

  constructor() {
    effect(() => {
      this.loadData();
    });
  }

  loadData() {
    this.loading = true;
    const params: any = {
      page: this.serverQuery.page(),
      size: this.serverQuery.pageSize(),
      search: this.serverQuery.search(),
    };

    if (this.serverQuery.sortBy()) {
      params.sort = `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}`;
    }

    this.service.getAll(params).subscribe({
      next: data => {
        this.data = (data as any).content ?? [];
        this.totalRecords = (data as any).totalElements ?? 0;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translate.instant('settings.paymentJustification.message.error.loading'),
        });
      },
    });
  }

  openDetailDrawer(row: SocieteDacListItem) {
    this.store.set('response', row);
    this.store.set('dto', row);
    this.detailDrawer = getPaymentJustificationDetailDrawerObject({
      onApprove: () => this.openApproveDialog(),
      onReject: () => this.openRejectDialog(),
      onCancel: () => this.drawerComponent.toggleVisible(),
    });
    this.drawerComponent.toggleVisible();
  }

  openApproveDialog() {
    this.dialogObject = getApproveConfirmDialog(
      () => {
        this.handleApprove();
        this.dialogComponent.toggleVisible();
      },
      () => this.dialogComponent.toggleVisible()
    );
    this.dialogComponent.toggleVisible();
  }

  openRejectDialog() {
    this.dialogObject = getRejectConfirmDialog(
      () => {
        this.handleReject();
        this.dialogComponent.toggleVisible();
      },
      () => this.dialogComponent.toggleVisible()
    );
    this.dialogComponent.toggleVisible();
  }

  handleApprove() {
    const row = this.store.get<SocieteDacListItem>('response');
    if (!row) return;

    this.service.approve(row.dacId, row.dacVer, row.societe.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: this.translate.instant('settings.paymentJustification.message.success.approved'),
        });
        this.drawerComponent.toggleVisible();
        this.loadData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translate.instant('settings.paymentJustification.message.error.approved'),
        });
      },
    });
  }

  handleReject() {
    const row = this.store.get<SocieteDacListItem>('response');
    if (!row) return;

    this.service.reject(row.dacId, row.dacVer, row.societe.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: this.translate.instant('settings.paymentJustification.message.success.rejected'),
        });
        this.drawerComponent.toggleVisible();
        this.loadData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translate.instant('settings.paymentJustification.message.error.rejected'),
        });
      },
    });
  }
}
